/**
 * Layout switcher component for sandbox detail page.
 * Allows users to switch between Classic and Alternative layouts.
 *
 * @module components/ui/LayoutSwitcher
 */

import { clsx } from 'clsx';
import { LayoutGrid, PanelBottom } from 'lucide-react';
import type { SandboxLayout } from '@/hooks/useSandboxLayout';

interface LayoutSwitcherProps {
  layout: SandboxLayout;
  onChange: (layout: SandboxLayout) => void;
}

const layouts: { value: SandboxLayout; label: string; icon: typeof LayoutGrid }[] = [
  { value: 'classic', label: 'Classic', icon: LayoutGrid },
  { value: 'alt', label: 'Alternative', icon: PanelBottom },
];

/**
 * Toggle-group component for selecting a sandbox detail page layout.
 */
export function LayoutSwitcher({ layout, onChange }: LayoutSwitcherProps) {
  return (
    <div className="flex items-center">
      <div className="inline-flex items-center gap-0.5 p-0.5 bg-gray-100 dark:bg-[#161616] border border-gray-200 dark:border-[#2a2a2a] rounded-lg">
        {layouts.map(({ value, label, icon: Icon }) => (
          <button
            key={value}
            type="button"
            onClick={() => onChange(value)}
            className={clsx(
              'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-all duration-150',
              layout === value
                ? 'bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-gray-100 shadow-xs'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            )}
            title={`${label} layout`}
          >
            <Icon className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">{label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
